'use client';

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { config } from '@/src/config';

export interface TurnstileWidgetHandle {
  reset: () => void;
}

export type TurnstileAppearance = 'always' | 'execute' | 'interaction-only';

interface TurnstileWidgetProps {
  onToken: (token: string | null) => void;
  action?: string;
  theme?: 'auto' | 'light' | 'dark';
  className?: string;
  // 'interaction-only' keeps the widget invisible unless Cloudflare needs
  // the visitor to act (the assistant panel).
  appearance?: TurnstileAppearance;
}

interface TurnstileGlobal {
  render: (
    container: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      'timeout-callback'?: () => void;
      action?: string;
      theme?: 'auto' | 'light' | 'dark';
      appearance?: TurnstileAppearance;
    },
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
  }
}

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';

let scriptPromise: Promise<void> | null = null;

const loadTurnstileScript = (): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      if (window.turnstile) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('turnstile script failed')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('turnstile script failed'));
    document.head.appendChild(script);
  });

  return scriptPromise;
};

const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  ({ onToken, action, theme = 'auto', className, appearance }, ref) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | null>(null);
    const onTokenRef = useRef(onToken);

    useEffect(() => {
      onTokenRef.current = onToken;
    }, [onToken]);

    useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          if (widgetIdRef.current && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current);
          }
        },
      }),
      [],
    );

    useEffect(() => {
      if (!config.TURNSTILE_SITE_KEY) return;

      let cancelled = false;

      loadTurnstileScript()
        .then(() => {
          if (cancelled) return;
          if (!containerRef.current || !window.turnstile) return;
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: config.TURNSTILE_SITE_KEY,
            callback: (token: string) => onTokenRef.current(token),
            'expired-callback': () => onTokenRef.current(null),
            'error-callback': () => onTokenRef.current(null),
            'timeout-callback': () => onTokenRef.current(null),
            action,
            theme,
            ...(appearance && { appearance }),
          });
        })
        .catch(() => {
          onTokenRef.current(null);
        });

      return () => {
        cancelled = true;
        if (widgetIdRef.current && window.turnstile) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {
            // widget may already be gone
          }
          widgetIdRef.current = null;
        }
      };
    }, [action, theme]);

    if (!config.TURNSTILE_SITE_KEY) return null;

    return <div ref={containerRef} className={className} />;
  },
);

TurnstileWidget.displayName = 'TurnstileWidget';

export default TurnstileWidget;
